// src/components/shared/GlobalHeader.js
//
// Pass 44 — the ONE global application header. Replaces the legacy Navbar.
// Motus logo (→ current workspace home) | Workspace switcher | Notifications |
// Account menu. Plus a visually-distinct DEV toolbar (emulator/dev only).
// It never renders a second product navigation; each workspace provides its own
// context nav below this header.

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../../AuthContext';
import { useWorkspace } from '../../WorkspaceContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useInboxMode } from '../../hooks/useInboxMode';
import { useStudentNotifications } from '../../hooks/useStudentComms';
import MotusLogo from './MotusLogo';
import MotusIcon from './MotusIcon';
import { canUsePlatformQaPreview } from '../../utils/platformAccess';

const C = {
  bar: '#FFFFFF', border: 'rgba(0,0,0,0.08)', t1: '#1C1B18', t2: '#57564F', t3: '#8A8980',
  card2: '#F5F4F0', blue: '#2563EB', red: '#DC2626', amber: '#D97706', amberDim: '#FEF3C7',
};
const font = "'DM Sans',system-ui,sans-serif";
const IS_DEV = process.env.REACT_APP_USE_EMULATOR === 'true' || process.env.NODE_ENV !== 'production';

function useOutside(ref, onClose) {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onClose]);
}

export default function GlobalHeader() {
  const navigate = useNavigate();
  const { userData, loginAsTestUser, setActiveOrganization } = useAuth() || {};
  const { workspaces, currentWorkspace, setWorkspace, WORKSPACES } = useWorkspace();
  const ws = WORKSPACES[currentWorkspace] || WORKSPACES.personal;

  // Pass 86P — canonical organization switcher for multi-org users (e.g. an owner of SAS Niseko AND motus-pilot-2).
  // Memberships are already on the resolved userData (no extra read); switching calls the ONE canonical
  // setActiveOrganization (which persists the preferred org + recomputes workspace entitlements) and re-syncs the
  // route to the current workspace's home so an org-scoped route cannot silently keep the previous orgId.
  const orgMemberships = (userData && Array.isArray(userData.organizationMemberships)) ? userData.organizationMemberships : [];
  const activeOrgId = (userData && (userData.orgId || userData.organizationId)) || '';
  const onSwitchOrg = (orgId) => {
    if (!orgId || orgId === activeOrgId || typeof setActiveOrganization !== 'function') return;
    const resolved = setActiveOrganization(orgId);
    if (resolved) { try { navigate((WORKSPACES[currentWorkspace] || WORKSPACES.personal).home); } catch (_) { /* ignore */ } }
  };

  // Pass 85.1B.1 (§2/§3/§23) — the badge obeys the SAME workspace communication contract as /inbox. A Personal (student)
  // workspace counts unread from the canonical participant+org scope (MODERN lane only; every unresolved lane → 0 and
  // ZERO listeners). A staff workspace counts org-scoped staff notifications (non-participant only). Student Preview and
  // any unresolved state surface no badge and start no listener. Only ONE listener is ever active.
  const { uid, mode, lane, studentOrg, studentPid, staffOrg } = useInboxMode();
  const studentN = useStudentNotifications(mode === 'student' && lane === 'modern' ? uid : '', studentOrg, studentPid);
  const staffN = useNotifications({ enabled: mode === 'staff' && !!staffOrg, organizationId: staffOrg });
  const unreadCount = mode === 'student'
    ? studentN.unreadCount
    : mode === 'staff'
      ? staffN.filter((n) => !n.read && !n.participantId).length
      : 0;

  const [wsOpen, setWsOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const wsRef = useRef(null); const acctRef = useRef(null);
  useOutside(wsRef, () => setWsOpen(false));
  useOutside(acctRef, () => setAcctOpen(false));

  // Pass 86P — never render a FAKE identity. Until canonical userData resolves, the name is empty and the avatar
  // shows a neutral placeholder (no "A"/"Account" flash). UNKNOWN is shown as neutral, never as invented initials.
  const hasIdentity = !!(userData && userData.name);
  const name = hasIdentity ? userData.name : '';
  const initials = hasIdentity ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '';

  const logout = async () => { try { await signOut(auth); } catch { /* ignore */ } navigate('/login'); };

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 200, fontFamily: font }}>
      <header style={{ height: 56, background: C.bar, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 14, padding: '0 20px' }}>
        {/* Logo → current workspace home */}
        <button onClick={() => navigate(ws.home)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }} title={`${ws.label} home`}>
          <MotusLogo width={92} />
        </button>
        {/* Pass 46F §14: persistent LOCAL/EMULATOR indicator — dev only (REACT_APP_USE_EMULATOR),
            never rendered in production. Prevents mistaking emulator fixtures for canonical SAS data. */}
        {process.env.REACT_APP_USE_EMULATOR === 'true' && (
          <span title="Connected to local Firebase emulators — this is development/test data, NOT canonical SAS production."
            style={{ fontFamily: font, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#B45309', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>
            ● LOCAL · EMULATOR
          </span>
        )}

        {/* Workspace switcher */}
        <div ref={wsRef} style={{ position: 'relative' }}>
          <button onClick={() => setWsOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontFamily: font, fontSize: 13.5, fontWeight: 700, color: C.t1 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: ws.color }} />
            {ws.label}
            <span style={{ color: C.t3, fontSize: 11 }}>▾</span>
          </button>
          {wsOpen && (
            <div style={{ position: 'absolute', top: 42, left: 0, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', minWidth: 210, padding: 6, zIndex: 300 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.t3, padding: '6px 10px' }}>Switch workspace</div>
              {workspaces.map((k) => {
                const w = WORKSPACES[k]; const active = k === currentWorkspace;
                return (
                  <button key={k} onClick={() => { setWsOpen(false); setWorkspace(k); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: active ? C.card2 : 'none', border: 'none', borderRadius: 8, padding: '9px 10px', cursor: 'pointer', fontFamily: font, fontSize: 14, fontWeight: active ? 700 : 500, color: C.t1, textAlign: 'left' }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: w.color }} />
                    {w.label}{active && <span style={{ marginLeft: 'auto', color: C.t3, fontSize: 12 }}>current</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Pass 86P — organization switcher (only for users who belong to more than one org). Selecting an org
            never grants access; it chooses among authorized memberships and recomputes workspace availability. */}
        {orgMemberships.length > 1 && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: C.card2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '5px 10px' }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: C.t3 }}>Org</span>
            <select
              data-testid="org-switcher"
              aria-label="Switch organization"
              value={activeOrgId}
              onChange={(e) => onSwitchOrg(e.target.value)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 13.5, fontWeight: 700, color: C.t1, outline: 'none' }}
            >
              {orgMemberships.map((m) => (
                <option key={m.organizationId} value={m.organizationId}>{m.organizationName || m.organizationId}</option>
              ))}
            </select>
          </label>
        )}

        <div style={{ flex: 1 }} />

        {/* Inbox — the surface now holds BOTH Notifications and Messages, so a restrained inbox/tray icon
            is used (no emoji, no bell, no gold). Quiet by default; a real unread badge appears ONLY when
            the signed-in user has genuine unread notifications. */}
        <style>{`
          .gh-inbox{position:relative;display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:10px;background:none;border:none;cursor:pointer;color:${C.t2};transition:background .15s}
          .gh-inbox:hover{background:${C.card2}}
          .gh-inbox:focus{outline:none}
          .gh-inbox:focus-visible{outline:2px solid ${C.blue};outline-offset:2px}
        `}</style>
        <button
          className="gh-inbox"
          onClick={() => navigate('/inbox?tab=notifications')}
          aria-label={unreadCount > 0 ? `Inbox, ${unreadCount} unread` : 'Inbox'}
          title="Inbox"
        >
          <MotusIcon name="inbox" size={19} color={C.t2} />
          {unreadCount > 0 && (
            <span aria-hidden="true" style={{ position: 'absolute', top: 3, right: 3, minWidth: 15, height: 15, background: C.red, color: '#fff', borderRadius: 8, fontSize: 9.5, fontWeight: 800, lineHeight: '15px', textAlign: 'center', padding: '0 3px', boxShadow: `0 0 0 2px ${C.bar}` }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>

        {/* Account menu (universal, workspace-independent) */}
        <div ref={acctRef} style={{ position: 'relative' }}>
          <button aria-label={hasIdentity ? `Account: ${name}` : 'Account'} onClick={() => setAcctOpen((o) => !o)} style={{ width: 34, height: 34, borderRadius: '50%', background: hasIdentity ? C.blue : C.card2, color: '#fff', border: hasIdentity ? 'none' : `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 800, fontSize: 12, fontFamily: font }}>{initials}</button>
          {acctOpen && (
            <div style={{ position: 'absolute', top: 42, right: 0, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', minWidth: 220, padding: 6, zIndex: 300 }}>
              <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                <div style={{ fontWeight: 700, color: C.t1, fontSize: 14 }}>{name || 'Your account'}</div>
                <div style={{ fontSize: 12, color: C.t3 }}>{userData?.email || ''}</div>
              </div>
              <MenuItem onClick={() => { setAcctOpen(false); navigate('/account'); }}>Account settings</MenuItem>
              {/* Pass 87.1 — platform QA/demo preview entry. Rendered ONLY for platform super-admin
                  (canUsePlatformQaPreview); ordinary SAS owner/admin/ops/instructor never see it. */}
              {canUsePlatformQaPreview(userData || {}) && (
                <MenuItem onClick={() => { setAcctOpen(false); navigate('/qa/preview'); }} dot={C.amber}>QA Preview</MenuItem>
              )}
              {workspaces.length > 1 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.t3, padding: '8px 12px 2px' }}>Switch workspace</div>
                  {workspaces.map((k) => (
                    <MenuItem key={k} onClick={() => { setAcctOpen(false); setWorkspace(k); }} dot={WORKSPACES[k].color}>{WORKSPACES[k].label}</MenuItem>
                  ))}
                </>
              )}
              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 4, paddingTop: 4 }}>
                <MenuItem onClick={logout} danger>Sign out</MenuItem>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* DEV-ONLY toolbar — visually distinct, never product navigation, dev/emulator only */}
      {IS_DEV && loginAsTestUser && (
        <div style={{ background: C.amberDim, borderBottom: `1px solid ${C.amber}`, display: 'flex', alignItems: 'center', gap: 8, padding: '4px 20px', fontSize: 12 }}>
          <span style={{ fontWeight: 800, color: C.amber, letterSpacing: 0.5 }}>DEV</span>
          <span style={{ color: C.t3 }}>login as:</span>
          {[['student', 'Student'], ['instructor', 'Instructor'], ['organization', 'Organization'], ['admin', 'Admin']].map(([role, label]) => (
            <button key={role} onClick={() => loginAsTestUser(role).then(() => navigate(role === 'organization' ? '/organization' : role === 'admin' ? '/admin' : '/dashboard')).catch(() => {})}
              style={{ background: '#fff', border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 6, padding: '2px 9px', cursor: 'pointer', fontFamily: font, fontWeight: 700, fontSize: 11.5 }}>{label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function MenuItem({ children, onClick, danger, dot }) {
  return (
    <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', borderRadius: 8, padding: '9px 12px', cursor: 'pointer', fontFamily: font, fontSize: 14, color: danger ? C.red : C.t1, textAlign: 'left' }}>
      {dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />}{children}
    </button>
  );
}
